import type { FC } from "react"
import {
	IconCheckOutline18 as Check,
	IconLink2SlashOutline18 as Link2Off,
} from "nucleo-ui-outline-18"
import { FixedSizeList } from "react-window"

import { cn } from "@productlane/lib/src/cn"
import { Avatar } from "@productlane/ui"
import { CommandItem } from "@productlane/ui/src/components/internal/command"

import type { SharedFilterItemsProps, WithQuery } from "./types"
import { useContactOptions } from "@/lib/use-entity-options"
import { useThreadsFilterState } from "../threads-filter-state"

export const LinkedPersonsFilterItems: FC<
	SharedFilterItemsProps & WithQuery
> = ({ onSelect, query }) => {
	const { personIds, addPerson, removePerson } = useThreadsFilterState()
	const { contacts: filteredPersons } = useContactOptions({
		query,
		selectedIds: personIds,
	})

	return (
		<>
			<CommandItem
				onSelect={() => {
					if (personIds.includes(null)) {
						removePerson(null)
					} else {
						addPerson(null)
					}
					onSelect()
				}}
				key="no-linked-person"
			>
				<div className="flex w-full items-center gap-2">
					<Link2Off className="h-4 w-4 shrink-0" />
					<span className="flex-1 truncate">No person linked</span>
					<Check
						className={cn("h-4 w-4 shrink-0", {
							"opacity-100": personIds.includes(null),
							"opacity-0": !personIds.includes(null),
						})}
					/>
				</div>
			</CommandItem>

			<hr className="my-2" />

			{filteredPersons.length ? (
				<div className="h-64">
					<FixedSizeList
						height={256}
						width="100%"
						itemCount={filteredPersons.length}
						itemData={filteredPersons}
						itemKey={(index, data) => data[index]?.id ?? index}
						itemSize={36}
						overscanCount={5}
						className="!h-full"
					>
						{({ data, index, style }) => {
							const person = data[index]

							if (!person) return null

							const name = person.name || person.email

							return (
								<CommandItem
									value={name}
									onSelect={() => {
										if (personIds.includes(person.id)) {
											removePerson(person.id)
										} else {
											addPerson(person.id)
										}
										onSelect()
									}}
									key={person.id}
									style={style}
								>
									<div className="flex w-full items-center gap-2">
										<Avatar image={person.imageUrl} name={name} size="xs" />
										<span className="flex-1 truncate">{name}</span>
										<Check
											className={cn("h-4 w-4 shrink-0", {
												"opacity-100": personIds.includes(person.id),
												"opacity-0": !personIds.includes(person.id),
											})}
										/>
									</div>
								</CommandItem>
							)
						}}
					</FixedSizeList>
				</div>
			) : (
				<div className="py-4 text-center text-sm">
					You haven&apos;t linked any people yet
				</div>
			)}
		</>
	)
}
